import "../FillInForm.styles.scss";
import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import _ from "lodash";
import { useGetPatients, usePublicSubmission } from "utils/hooks";
import { User, Group, Assignment, Field } from "types";
import InfoSection from "./InfoSection";
import PublicInfoSection from "./PublicInfoSection";
import {
  displayFields,
  editFields,
} from "pages/AppPages/TherapistPages/AssignmentBuilder/components/AssignmentBuilderMenu/fieldTypes";
import HintAndError from "pages/AppPages/PatientPages/AssignmentsForPatients/fields/CommonComponents/HintAndError";
import {
  DisplayFields,
  EditableFields,
} from "pages/AppPages/PatientPages/AssignmentsForPatients/fields";
import { Button } from "components";
import { useAssignmentStore } from "pages/AppPages/PatientPages/AssignmentsForPatients/assignmentStore";
import {
  createSubmission,
  updateSubmission,
} from "pages/AppPages/PatientPages/AssignmentsForPatients/useSubmissionsApi";
import { useCurrentUser, useFetchSubmissions } from "utils/hooks";
import PatientData from "./PatientData";
import SuccessSubmitMsg from "./SuccessSubmitMsg";
import { createSubmissionAction } from "store/actions/submissions";
import { updateUserPinVerification } from "store/actions/auth";

type PublicAssignmInfo = {
  therapistId: string;
  therapistName: string;
  therapistEmail: string;
  therapistIcon: string;
  practiceName: string;
  practiceLogo: string;
  groupName: string;
  assignment: Assignment;
};
interface FillAssignmentProps {
  singlePatient: User | null;
  selectedPatient: { label: string; value: string } | null;
  group: Group | null;
  assignment: Assignment;
  hideFromPatient: boolean;
  onClose?: () => void;
  requestPin: boolean;
  publicLink?: boolean;
  publicAssignmInfo?: PublicAssignmInfo;
}
type PatientInfo = {
  firstName: string;
  lastName: string;
  email: string;
};

const FillAssignment = ({
  singlePatient,
  selectedPatient,
  group,
  assignment,
  hideFromPatient,
  onClose,
  requestPin,
  publicLink,
  publicAssignmInfo,
}: FillAssignmentProps) => {
  const { patient } = useGetPatients({ patientId: selectedPatient?.value });
  const { user } = useCurrentUser();
  const { state, dispatch } = useAssignmentStore();
  const reduxDispatch = useDispatch();
  const { activeSection } = state;
  const { t } = useTranslation("common");
  const { createPublicSubmission, updatePublicSubmission } =
    usePublicSubmission();
  const { fetchGroupSubmissions } = useFetchSubmissions();
  const [isWide, setIsWide] = useState(state.isWideData || false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [patientInfo, setPatientInfo] = useState<PatientInfo>({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [successMsg, setSuccessMsg] = useState(false);
  const isFinalSection = state.isFinalSection();

  useEffect(() => {
    if (state.initialized && !activeSection?.slug) {
      dispatch({
        location: {
          assignmentSlug: assignment?.slug,
          sectionSlug: "",
        },
      });
    }
  });

  useEffect(() => {
    if (publicLink) return;

    const addPinCheck = () => {
      requestPin && reduxDispatch(updateUserPinVerification(false));
    };
    addEventListener("beforeunload", addPinCheck);
    return () => {
      removeEventListener("beforeunload", addPinCheck);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hasCheckboxGrid = activeSection?.fields?.some(
      (field) => field.type === "CHECKBOX_GRID"
    );
    const isAssessment = state?.activeAssignment?.category === "ASSESSMENT";
    if ((hasCheckboxGrid || isAssessment) && !isWide) {
      setIsWide(true);
      dispatch({ isWideData: true }); // Update state
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection, isWide, dispatch]);

  const saveSubmission = useCallback(
    async (isDraft: boolean) => {
      const responseFields = Object.keys(state.responses).map((key) => {
        const responseField = state.responses[key];
        // don't want to send preFilled to the server!! Also, this will remove
        // the preFilled indicator for the user as soon as they edit a field
        delete responseField.preFilled;
        return responseField;
      });

      const getUserId = () => {
        if (user && user.type === "patient") {
          return user.id;
        }
        if (user && user.type === "therapist") {
          return singlePatient ? singlePatient.uuid : patient?.uuid || "";
        } else {
          return "";
        }
      };
      //check if there's an existing draft
      if (!state.draftId) {
        const createSubmissionFetch =
          user && !publicLink ? createSubmission : createPublicSubmission;
        return await createSubmissionFetch(
          {
            userId: getUserId(),
            authorId: user ? user.id : "",
            userName: `${patientInfo.firstName} ${patientInfo.lastName}`,
            userEmail: patientInfo.email,
            assignmentId: state.activeAssignment?.uuid || "",
            assignmentLabel: state.activeAssignment?.label || "",
            privacy: state.isPrivate,
            groupPrivacy: state.isGroupPrivate,
            isDraft: isDraft,
            isWideData: isWide,
            hideFromPatient: hideFromPatient ? true : false,
            therapistId: publicAssignmInfo?.therapistId,
          },
          responseFields,
          user?.activeOrganizationId || "NA"
        );
      } else {
        const updateSubmissionFetch =
          user && !publicLink ? updateSubmission : updatePublicSubmission;
        return await updateSubmissionFetch(
          {
            userId: getUserId(),
            authorId: user ? user.id : "",
            uuid: state.draftId,
            userName: `${patientInfo.firstName} ${patientInfo.lastName}`,
            userEmail: patientInfo.email,
            assignmentId: state.activeAssignment?.uuid || "",
            assignmentLabel: state.activeAssignment?.label || "",
            privacy: state.isPrivate,
            groupPrivacy: state.isGroupPrivate,
            isDraft: isDraft,
            isWideData: isWide,
            hideFromPatient: hideFromPatient ? true : false,
            therapistId: publicAssignmInfo?.therapistId,
          },
          responseFields,
          user?.activeOrganizationId || "NA"
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  const handleChange = (field: Field, index: number, value: any) => {
    if (!activeSection) return;
    state.responses[field.uuid as string].value = value;
    state.responses[field.uuid as string].preFilled = false;
    activeSection.fields[index].error = false;
    dispatch({ activeSection, responses: state.responses });
  };

  const handleSubmit = async (isDraft: boolean, btnName?: string) => {
    if (!activeSection) return;
    if (
      publicLink &&
      (!patientInfo.firstName || !patientInfo.lastName || !patientInfo.email)
    ) {
      toast.warn(t("share_link.name_email_warn"));
      return;
    }
    try {
      const { validatedSection, isValid } =
        state.validateSection(activeSection);
      dispatch({ activeSection: validatedSection, isSectionValid: isValid });
      if (!isValid) {
        toast.warning("Please fill out all required fields before proceeding");
        return;
      }
      setIsSubmitting(true);
      const submission = await saveSubmission(isDraft);
      dispatch({ activeDraft: submission });
      let hasSubmission = false;
      state.submissions.forEach((sub, index) => {
        if (sub.uuid === submission.uuid) {
          state.submissions[index] = submission;
          hasSubmission = true;
        }
      });
      if (!hasSubmission) {
        state.submissions.push(submission);
      }

      dispatch({ submissions: state.submissions });
      setIsSubmitting(false);

      if (isDraft) {
        toast(`Your ${state.activeAssignment?.label} draft has been saved!`);
      } else {
        toast(`Your ${state.activeAssignment?.label} has been submitted!`);
        publicLink && setSuccessMsg(true);
        if (!publicLink) {
          reduxDispatch(createSubmissionAction(submission));
          group && fetchGroupSubmissions(group.uuid);
        }
        onClose && onClose();
      }

      if (btnName) {
        let section;
        if (btnName === "btn-next-section") section = state.getNextSection();
        if (btnName === "btn-prev-section")
          section = state.getPreviousSection();
        if (section) {
          dispatch({
            location: {
              assignmentSlug: assignment.slug,
              sectionSlug: section.slug,
            },
          });
        }
      }
    } catch (error) {
      console.log(error);
      toast.warn(t("errors.something_went_wrong"));
    }
  };

  return (
    <div className="fill-modal_container">
      {publicLink && publicAssignmInfo ? (
        <PublicInfoSection publicAssignmentInfo={publicAssignmInfo} />
      ) : (
        <InfoSection
          patient={singlePatient ? singlePatient : patient}
          group={group}
        />
      )}
      {!successMsg && (
        <div className="fill-modal_form">
          {publicLink && activeSection?.order === 1 && (
            <PatientData
              patientInfo={patientInfo}
              setPatientInfo={setPatientInfo}
            />
          )}
          {activeSection?.description && (
            <div className="fill-modal_form_description">
              {activeSection.description}
            </div>
          )}
          {activeSection?.fields?.map((field, index) => {
            if (field.status === "HIDDEN") {
              return null;
            }
            const hideHint =
              field.type === "SCALE" || !field.hint || field.hint === "null";
            if (displayFields?.includes(field.type)) {
              return (
                <div className={`fill-modal_form_field`} key={field.uuid}>
                  <DisplayFields key={field.uuid} field={field} />
                </div>
              );
            }
            if (editFields?.includes(field.type)) {
              return (
                <div className={`fill-modal_form_field`} key={field.uuid}>
                  <EditableFields
                    field={field}
                    defaultValue={state.responses[field.uuid as string]?.value}
                    onChange={_.throttle(
                      (value: any) => handleChange(field, index, value),
                      500
                    )}
                  />
                  <HintAndError
                    error={field.error}
                    hint={hideHint ? "" : field.hint}
                  />
                </div>
              );
            }
            return (
              <div className={`fill-modal_form_field`} key={field.uuid}>
                {field.label}
                <div>{field.value}</div>
              </div>
            );
          })}
          <div className="fill-modal_form_buttons">
            <Button
              value=""
              name="btn-prev-section"
              pill
              icon="ArrowLeftBold"
              extraClass="fill-modal_form_arrow"
              disabled={
                (activeSection?.order && activeSection.order === 1
                  ? true
                  : false) || isSubmitting
              }
              onClick={async () => {
                await handleSubmit(true, "btn-prev-section");
              }}
            />
            <Button
              value=""
              name="btn-next-section"
              pill
              icon="ArrowRightBold"
              extraClass="fill-modal_form_arrow"
              disabled={isFinalSection || isSubmitting}
              onClick={async () => await handleSubmit(true, "btn-next-section")}
            />
            <Button
              value="submit"
              name="btn-submit"
              pill
              extraClass="submit-button"
              disabled={!isFinalSection || isSubmitting}
              isLoading={isSubmitting}
              onClick={() => handleSubmit(false)}
            />
          </div>
        </div>
      )}
      {successMsg && <SuccessSubmitMsg patientInfo={patientInfo} />}
    </div>
  );
};

export default FillAssignment;
